/**
 * rail-vertical-fit — ДЕТЕРМИНИРОВАННАЯ вертикальная подгонка боковой панели.
 *
 * Когда вторичная строка панели (вердикт «Зачтено: N» на итоге; в перспективе —
 * любой descriptive-ряд rail'а) НЕ помещается в высоту панели, СНАЧАЛА чуть ужимает
 * зазор между кнопками (отыгрывает вертикаль), и ТОЛЬКО если и тогда не влезает —
 * прячет вторичную строку (избегает коллизии с кнопками, как было раньше).
 *
 * owner-directive 2026-06-05 verbatim: «сначала расстояние между кнопками немного
 * попыталось уменьшиться и если в таком случае «Зачтено: N» влезает — принимаем,
 * если всё-равно не влезает, то этот текст не показывается, т.е. как сейчас,
 * избегает коллизии».
 *
 * Зачем МОДУЛЬ + ИЗМЕРЕНИЕ, а не чистый CSS: «показать или схлопнуть» — условие,
 * зависящее от ФАКТИЧЕСКОЙ высоты, которая ПЛАВАЕТ (адресная строка мобильного
 * браузера меняет высоту ландшафта; вердикт приходит async после ответа сервера).
 * Прежний механизм (postament `.postament-secondary { flex-shrink:1; overflow:
 * hidden }`) схлопывал вердикт МОЛЧА и НЕДЕТЕРМИНИРОВАННО — то влезает, то нет при
 * «том же» месте (owner-bug 2026-06-04). Здесь решение принимается ИЗМЕРЕНИЕМ
 * (engine-identical, на каждом settle): «если есть место — вердикт виден».
 *
 * Работает ТОЛЬКО в rail-раскладке. В компакте (узкая панель → ⓘ/☰) вторичная
 * строка и так скрыта CSS'ом (.rail-is-compact) — модуль не вмешивается.
 *
 * Generic: экран передаёт элементы (панель / вторичная строка / контейнер кнопок)
 * и имена классов-хуков; механика (замер, лестница, пересчёт на resize/geometry/
 * fonts/смену вердикта) — здесь. Стили хуков — action-rail.css (tight) + postament.css
 * (collapsed).
 */

import { VIEWPORT_APPLIED_EVENT } from './viewport-sizer.js';

export interface RailVerticalFitOptions {
  /** Боковая панель (rail-раскладка: флекс-колонка фиксированной высоты). */
  readonly rail: HTMLElement;
  /** Вторичная строка (вердикт/KPI) — её показываем/прячем последней ступенью. */
  readonly secondary: HTMLElement;
  /** Контейнер кнопок (.action-rail) — на него вешается `tightClass` (ужать зазор). */
  readonly actions: HTMLElement;
  /** Класс на `actions`: уменьшает зазор между кнопками (action-rail.css). */
  readonly tightClass: string;
  /** Класс на `secondary`: прячет вторичную строку (postament.css). */
  readonly collapsedClass: string;
  /** Класс компакта на `rail` — если стоит, модуль бездействует (строка скрыта CSS). */
  readonly compactClass: string;
}

export interface RailVerticalFitHandle {
  /** Пересчитать немедленно (напр. после прихода async-вердикта). */
  refresh(): void;
  detach(): void;
}

/** Допуск подгонки (px): глушит дребезг на субпиксельных расхождениях движков. */
const FIT_EPS_PX = 1;

/**
 * Привязать вертикальную подгонку к панели экрана. `root` — корень экрана
 * (наблюдается ResizeObserver'ом). Возвращает handle с refresh()/detach().
 */
export function attachRailVerticalFit(
  root: HTMLElement,
  opts: RailVerticalFitOptions,
): RailVerticalFitHandle {
  const { rail, secondary, actions, tightClass, collapsedClass, compactClass } = opts;

  // Суммарная «нужная» высота содержимого панели при ТЕКУЩЕМ состоянии (зазор/перенос).
  // secondary меряем по scrollHeight (натуральная высота — даже если flex-backstop её
  // ужал/обрезал); остальные дети — по offsetHeight (фактический рендер, учитывает
  // перенос лейблов и текущий зазор кнопок). + межрядные gap'ы панели (rowGap резолвится
  // в px — кросс-браузерно безопасно, в отличие от calc-токена). margin-top:auto кнопок
  // — это слабина (slack), в «нужную» высоту НЕ входит: need ≤ railH ⇔ slack ≥ 0.
  const measureNeed = (): number => {
    const gap = parseFloat(getComputedStyle(rail).rowGap) || 0;
    let total = 0;
    let count = 0;
    for (const child of Array.from(rail.children) as HTMLElement[]) {
      if (child.classList.contains('rail-compact')) continue; // хост ⓘ/☰ — вне потока обвязки
      if (getComputedStyle(child).display === 'none') continue;
      total += child === secondary ? child.scrollHeight : child.offsetHeight;
      count += 1;
    }
    return total + gap * Math.max(0, count - 1);
  };

  const update = (): void => {
    const docEl = document.documentElement;
    const inRail = docEl.dataset.layout === 'rail' && !rail.classList.contains(compactClass);
    // База: вердикт показан, обычный зазор. (И в stacked/компакте оставляем так —
    // там действуют другие правила; наши хуки сняты.)
    actions.classList.remove(tightClass);
    secondary.classList.remove(collapsedClass);
    if (!inRail) return;

    const railH = rail.clientHeight;
    if (railH <= 0) return;

    if (measureNeed() <= railH + FIT_EPS_PX) return; // влезает как есть → вердикт виден
    actions.classList.add(tightClass); // шаг 1: немного ужать зазор между кнопками
    if (measureNeed() <= railH + FIT_EPS_PX) return; // влезло → принимаем (tight + вердикт)
    actions.classList.remove(tightClass); // шаг 2: всё равно не лезет —
    secondary.classList.add(collapsedClass); // прячем вердикт (избегаем коллизии)
  };

  // rAF-дебаунс + trailing-замер после переверстки (autofit/перенос лейблов, смена
  // раскладки — не мгновенны). Зеркалит дисциплину compact-rail.
  let raf = 0;
  let trailing = 0;
  const run = (): void => {
    raf = 0;
    update();
  };
  const schedule = (): void => {
    if (raf) return;
    raf = requestAnimationFrame(run);
  };
  const scheduleSettled = (): void => {
    schedule();
    if (trailing) clearTimeout(trailing);
    trailing = window.setTimeout(schedule, 160);
  };

  scheduleSettled();
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(scheduleSettled);
    ro.observe(root);
  }
  // Async-вердикт: applyDynamic меняет текст/`hidden` у #result-status внутри secondary
  // → пересчитать (иначе строка появляется после последнего замера и схлопывается/нет
  // недетерминированно).
  let mo: MutationObserver | null = null;
  if (typeof MutationObserver !== 'undefined') {
    mo = new MutationObserver(scheduleSettled);
    mo.observe(secondary, { childList: true, subtree: true, characterData: true, attributes: true });
  }
  window.addEventListener(VIEWPORT_APPLIED_EVENT, scheduleSettled);
  if (typeof document !== 'undefined' && 'fonts' in document) {
    void document.fonts.ready.then(scheduleSettled);
  }

  return {
    refresh(): void {
      scheduleSettled();
    },
    detach(): void {
      if (raf) cancelAnimationFrame(raf);
      if (trailing) clearTimeout(trailing);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      window.removeEventListener(VIEWPORT_APPLIED_EVENT, scheduleSettled);
      actions.classList.remove(tightClass);
      secondary.classList.remove(collapsedClass);
    },
  };
}
